import React, { ReactNode } from "react";
import { mdiCheckboxBlankOutline, mdiCheckboxMarked, mdiChevronDown, mdiChevronRight } from "@mdi/js";
import { cn, getFocusRingStyles } from "../../../styles";
import { ThemeColor } from "../../../props";
import { Icon } from "../../../atoms/Icon";
import { useDataTableContext } from "../context";
import { DataTableRow, DataTableColumn } from "../types";
import { Cell } from "./Cell";
import { FROZEN_SELECT_ID, FROZEN_EXPAND_ID } from "../hooks/useFrozenColumns";
import {
  frozenCellStyle,
  frozenCellClassName,
  rowBackgroundClassName,
} from "./frozenStyle";

export interface RowProps<TRow> {
  row: DataTableRow<TRow>;
  index: number;
  className?: string;
  /** Renders cell *content* only; the Cell itself (freezing, alignment, width) stays owned by the Row. */
  renderCellContent?: (
    row: DataTableRow<TRow>,
    column: DataTableColumn<TRow>,
  ) => ReactNode;
  measureElement?: (element: Element | null) => void;
}

export function Row<TRow>({
  row,
  index,
  className,
  renderCellContent,
  measureElement,
}: RowProps<TRow>) {
  const { table, loading, dense, rowClasses, rowBackgroundClass, selection, renderRowExpansion, expansion, alwaysExpanded, color, frozen } =
    useDataTableContext<TRow>();

  const isSelected = selection?.isSelected(row as TRow) ?? false;
  const isSelectableRow = selection?.isSelectable(row as TRow) ?? true;
  const collapsible = !alwaysExpanded && !!expansion && !!renderRowExpansion;
  const isExpanded = alwaysExpanded
    ? true
    : expansion
      ? expansion.isExpanded(row._meta.id)
      : (row._meta?.isExpanded ?? false);
  const customClasses = rowClasses?.(row as TRow, index);

  const isOddStripe = !isSelected && index % 2 === 1;
  // One opaque background for the whole row, so the sticky cells that repaint it
  // cannot drift from the rest of the row.
  const rowBg =
    rowBackgroundClass?.(row as TRow, index) ??
    rowBackgroundClassName(color, isSelected, isOddStripe);
  const selectFreezeInfo = frozen.getFreezeInfo(FROZEN_SELECT_ID);
  const expandFreezeInfo = frozen.getFreezeInfo(FROZEN_EXPAND_ID);

  const handleToggleSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (selection && isSelectableRow) {
      selection.toggle(row as TRow);
    }
  };

  const visibleColumns = table.columns.filter((col) => !col.hidden);
  const colSpan =
    visibleColumns.length +
    (selection ? 1 : 0) +
    (collapsible ? 1 : 0);

  return (
    <>
      <tr
        ref={measureElement}
        data-index={index}
        onClick={collapsible ? () => expansion!.toggle(row._meta.id) : undefined}
        className={cn(
          "group border-b border-back-200/40 dark:border-back-900/50",
          rowBg,
          loading && "opacity-10",
          collapsible && "cursor-pointer hover:bg-back-100/60 dark:hover:bg-back-800/40",
          customClasses,
          className,
        )}
      >
        {selection && (
          <td
            className={cn("p-0", selectFreezeInfo && rowBg, frozenCellClassName(selectFreezeInfo))}
            style={frozenCellStyle(selectFreezeInfo)}
          >
            <button
              onClick={handleToggleSelection}
              disabled={!isSelectableRow}
              className={cn(
                "flex items-center justify-center w-full cursor-pointer",
                "outline-none focus-visible:ring-2 focus-visible:ring-inset rounded",
                getFocusRingStyles(ThemeColor.primary),
                dense ? "px-1.5 py-0.5" : "px-2 py-1",
                isSelected
                  ? "text-primary-600"
                  : isSelectableRow
                    ? "text-front-200 group-hover:text-front-300"
                    : "text-front-100 cursor-not-allowed",
              )}
            >
              <Icon
                path={isSelected ? mdiCheckboxMarked : mdiCheckboxBlankOutline}
                size={18}
              />
            </button>
          </td>
        )}
        {visibleColumns.map((column) => (
          <Cell
            key={column.id}
            column={column}
            row={row}
            freezeInfo={frozen.getFreezeInfo(column.id)}
            freezeBackgroundClassName={rowBg}
          >
            {renderCellContent?.(row, column)}
          </Cell>
        ))}
        {collapsible && (
          <td
            className={cn(
              "w-8 text-center text-front-400",
              dense ? "py-0.5" : "py-1",
              expandFreezeInfo && rowBg,
              frozenCellClassName(expandFreezeInfo),
            )}
            style={frozenCellStyle(expandFreezeInfo)}
            onClick={(e) => e.stopPropagation()}
          >
            <Icon path={isExpanded ? mdiChevronDown : mdiChevronRight} size={18} />
          </td>
        )}
      </tr>
      {isExpanded && renderRowExpansion && (
        <tr className="border-b border-back-200/40 dark:border-back-900/50 bg-back-50 dark:bg-back-900">
          <td colSpan={colSpan}>{renderRowExpansion(row as TRow)}</td>
        </tr>
      )}
    </>
  );
}
